import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import parse from "html-react-parser";
import Link from "next/link";
import { Fragment } from "react";
import { getLastActiveAtTime } from "../../../../helpers/conditional_rendering";
import SingleBenefitBox from "./SingleJobBenefit/SingleBenefitBox";
import {
  AdditionalInfoBody,
  AdditionalInfoTitle,
  ChipStyled,
  DivStyled,
  SectionContainer,
  SectionTitle,
  TypographyStyled,
} from "./styles";

function SingleJobDescription(props) {
  const { job, handleHref } = props;

  const { benefits } = job.company || {};

  const belowTablet = useMediaQuery("(max-width: 767px)");

  return (
    <Fragment>
      {/* JOB DESCRIPTION SECTION */}
      <SectionContainer aria-label="job-description-section">
        <SectionTitle variant="h1">Job Description</SectionTitle>
        {parse(job?.description)}
      </SectionContainer>
      <Divider style={{ margin: "30px 0px" }} />

      {/* JOB REQUIREMENT SECTION */}
      {job?.requirements ? (
        <Fragment>
          <SectionContainer aria-label="job-requirement-section">
            <SectionTitle variant="h1">Job Requirements</SectionTitle>
            {parse(job?.requirements)}
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* WORK ARRANGEMENT SECTION */}
      {job && job?.globalHirePreferences.enabled ? (
        <div id="job-work-arrangement">
          <SectionContainer aria-label="right-to-work-section">
            <SectionTitle variant="h1">Right to Work Requirements</SectionTitle>

            <ul>
              {job?.globalHirePreferences.rightToWorkOpenToSponsor ? (
                <li>
                  The company is open to sponsoring a working visa for qualified
                  candidates (subject to T&Cs)
                </li>
              ) : null}
              {job?.globalHirePreferences.rightToWorkLocalCitizen ||
              job?.globalHirePreferences.rightToWorkPermanentCitizen ||
              job?.globalHirePreferences.rightToWorkWorkVisaOrEmploymentPass ? (
                <>
                  <li>
                    Candidates with an existing right to work in the country are
                    preferred
                  </li>
                  <ul type="disc">
                    {job?.globalHirePreferences.rightToWorkLocalCitizen ? (
                      <li>Local citizens of this country</li>
                    ) : null}
                    {job?.globalHirePreferences.rightToWorkPermanentCitizen ? (
                      <li>Permanent residents (PR) of this country</li>
                    ) : null}
                    {job?.globalHirePreferences
                      .rightToWorkWorkVisaOrEmploymentPass ? (
                      <li>
                        Candidates who already have a work permit for this
                        country
                      </li>
                    ) : null}
                  </ul>
                </>
              ) : null}
            </ul>
          </SectionContainer>

          <SectionContainer aria-label="work-arrangement-section">
            <SectionTitle variant="h1">Working Arrangement</SectionTitle>

            <ul>
              {job?.globalHirePreferences.workingArrangementRemote ? (
                <li>Remote</li>
              ) : null}
              {job?.globalHirePreferences.workingArrangementOnSite ? (
                <li>On Site</li>
              ) : null}
              {job?.globalHirePreferences.workingArrangementHybrid ? (
                <li>Hybrid (Both Remote and On Site)</li>
              ) : null}
            </ul>
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </div>
      ) : null}

      {/* JOB SKILL SECTION */}
      {Array.isArray(job?.skills) && job?.skills.length > 0 ? (
        <Fragment>
          <SectionContainer aria-label="job-skill-requirement-section">
            <SectionTitle variant="h1">Skills</SectionTitle>
            <DivStyled container flexDirection={"row"}>
              {job?.skills.map((skill, index) => (
                <ChipStyled label={skill.name} key={`job-skill-${index}`} />
              ))}
            </DivStyled>
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* COMPANY BENEFIT SECTION */}
      {Array.isArray(benefits) && benefits.length > 0 ? (
        <Fragment>
          <section aria-label="job-benefit-section">
            <SectionTitle variant="h1">Company Benefits</SectionTitle>
            <DivStyled
              display={belowTablet ? "flex" : "grid"}
              gridTemplateColumns={"repeat(3, 1fr)"}
              gap={"15px"}
              sx={{
                overflowX: belowTablet ? "scroll" : null,
              }}
            >
              {benefits.map((benefit) => (
                <SingleBenefitBox
                  key={`job-benefit-${benefit.title}`}
                  jobBenefit={benefit}
                />
              ))}
            </DivStyled>
          </section>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* JOB ADDITIONAL INFO SECTION */}
      <section aria-label="job-additional-info-section">
        <SectionTitle variant="h1">Additional Info</SectionTitle>
        <DivStyled container columns={12} gap={"15px"}>
          {getLastActiveAtTime(job?.company?.lastActiveAt) !== null ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                Company Activity
              </AdditionalInfoTitle>
              <AdditionalInfoBody variant="body1">
                Last active - {getLastActiveAtTime(job?.company?.lastActiveAt)}
              </AdditionalInfoBody>
            </DivStyled>
          ) : null}

          {job?.jobType.toString().toLowerCase().includes("internship") ||
          (job.minYearsExperience <= 0 && job.maxYearsExperience === 0) ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                Experience Level
              </AdditionalInfoTitle>
              <Link
                href={handleHref("experience", job)}
                style={{ textDecoration: "none", color: "currentColor" }}
              >
                <AdditionalInfoBody variant="body1">
                  #NoExperienceNeeded
                </AdditionalInfoBody>
              </Link>
            </DivStyled>
          ) : job.minYearsExperience === null ||
            job.maxYearsExperience === null ? null : (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                Experience Level
              </AdditionalInfoTitle>
              <Link
                href={handleHref("experience", job)}
                style={{ textDecoration: "none", color: "currentColor" }}
              >
                <AdditionalInfoBody variant="body1">
                  {job?.minYearsExperience} - {job?.maxYearsExperience} Years of
                  Experience
                </AdditionalInfoBody>
              </Link>
            </DivStyled>
          )}

          {job?.careerLevel ? (
            <DivStyled item xs={8} tablet={3}>
              <AdditionalInfoTitle variant="h2">
                Career Level
              </AdditionalInfoTitle>
              <AdditionalInfoBody variant="body1">
                {job?.careerLevel}
              </AdditionalInfoBody>
            </DivStyled>
          ) : null}

          {Array.isArray(job?.tracks) && job?.tracks.length > 0 ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                Job Specialisation
              </AdditionalInfoTitle>

              <Link
                href={handleHref("specialisation", job)}
                style={{ textDecoration: "none", color: "currentcolor" }}
              >
                <DivStyled container flexDirection={"row"}>
                  <TypographyStyled variant="body1" key={`job-specialisation`}>
                    {job?.tracks?.map(
                      (track, index) =>
                        track.title +
                        (index !== job?.tracks?.length - 1 ? ", " : "")
                    )}
                  </TypographyStyled>
                </DivStyled>
              </Link>
            </DivStyled>
          ) : null}
        </DivStyled>
      </section>
    </Fragment>
  );
}

export default SingleJobDescription;
